import React, { useState, useEffect } from 'react';
import { Grid, TextField, FormControlLabel, Checkbox, Box, Typography, IconButton, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import { VOBFormData } from '../types';
import { Vob } from '@finpay/estimation-types';

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

// New styled component for numeric fields with right-aligned text
const VOBNumericField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(0),
    '& input': {
        textAlign: 'right',
    }
}));

// Add styled component for the checkbox label to prevent wrapping
const NoWrapFormControlLabel = styled(FormControlLabel)({
    whiteSpace: 'nowrap',
    margin: 0,
    '& .MuiFormControlLabel-label': {
        fontSize: '1.2rem',
    }
});

// Styled component for the framed section
const FramedSection = styled(Box)(({ theme }) => ({
    border: '2px solid #e0e0e0',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    position: 'relative',
}));

// Styled component for frame label
const FrameLabel = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'hasButton',
})<{ hasButton?: boolean }>(({ theme, hasButton }) => ({
    position: 'absolute',
    top: '-0.75rem',
    left: hasButton ? '36px' : '10px', // Move label right if there's a button
    backgroundColor: '#fff',
    padding: '0 5px',
    fontSize: '1.2rem',
    fontWeight: '400',
    color: 'rgba(0, 0, 0, 0.6)',
    lineHeight: '1.5rem',
}));

// Styled component for frame label button
const FrameLabelButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '-0.75rem',
    left: '10px',
    transform: 'translateY(-1px)',
    width: '20px',
    height: '20px',
    minWidth: '20px',
    padding: 0,
    backgroundColor: '#fff',
    zIndex: 1,
}));

// Styled component for in-field button
const InFieldNoButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'isPressed',
})<{ isPressed?: boolean }>(({ theme, isPressed }) => ({
    width: '20px',
    height: '20px',
    minWidth: '20px',
    padding: 0,
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: isPressed ? '#fff' : '#f5f5f5',
    boxShadow: isPressed ? 'inset 0 2px 4px rgba(0,0,0,0.15)' : 'none',
    transform: isPressed ? 'translateY(1px)' : 'none',
    '&:hover': {
        backgroundColor: isPressed ? '#fff' : '#e0e0e0',
    }
}));

// No Symbol SVG component
const NoSymbol = ({ isPressed }: { isPressed: boolean }) => {
    const color = isPressed ? '#ff0000' : '#999999';
    
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="7" stroke={color} strokeWidth="1.5" fill="none" />
            <line x1="3.5" y1="3.5" x2="12.5" y2="12.5" stroke={color} strokeWidth="1.5" />
        </svg>
    );
};

// Helper function to format number with 2 decimal places if not a whole number
const formatNumberValue = (value: string): string => {
    if (!value) return value;

    if (value.endsWith('.')) {
        return value;
    }
    
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    
    // Check if the number is a whole number
    return Number.isInteger(num) ? num.toString() : num.toFixed(2);
};

interface VOBFormFieldsProps {
    formData: VOBFormData;
    onInputChange: (field: keyof VOBFormData, value: string | boolean) => void;
    currentVob?: Vob | null;
    deductibleError?: boolean;
    remainingError?: boolean;
    oopmError?: boolean;
    oopmRemainingError?: boolean;
    deductibleErrorMessage?: string;
    remainingErrorMessage?: string;
    oopmErrorMessage?: string;
    oopmRemainingErrorMessage?: string;
    copayError?: boolean;
    maxDaysError?: boolean;
    coinsuranceError?: boolean;
    copayErrorMessage?: string;
    maxDaysErrorMessage?: string;
    coinsuranceErrorMessage?: string;
    resetButtonStates?: boolean;
    setButtonStates?: {
        isDeductibleButtonPressed: boolean;
        isOopmButtonPressed: boolean;
        isServiceLimitsButtonPressed: boolean;
        isMaxDaysButtonPressed: boolean;
        isCoinsuranceButtonPressed: boolean;
    };
}

// Styled component for field label button
const FieldLabelButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: '-8px',
    left: '12px',  // Position it to the left of the "M" in "Max Days"
    width: '16px',
    height: '16px',
    minWidth: '16px',
    padding: 0,
    backgroundColor: '#fff',
    zIndex: 1,
}));

// Styled wrapper for input field with label button
const LabelButtonWrapper = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
}));

export const VOBFormFields: React.FC<VOBFormFieldsProps> = ({
    formData,
    onInputChange,
    currentVob,
    deductibleError,
    remainingError,
    oopmError,
    oopmRemainingError,
    deductibleErrorMessage,
    remainingErrorMessage,
    oopmErrorMessage,
    oopmRemainingErrorMessage,
    copayError,
    maxDaysError,
    coinsuranceError,
    copayErrorMessage,
    maxDaysErrorMessage,
    coinsuranceErrorMessage,
    resetButtonStates,
    setButtonStates
}) => {
    // State to track button pressed states
    const [isDeductibleButtonPressed, setIsDeductibleButtonPressed] = useState(false);
    const [isOopmButtonPressed, setIsOopmButtonPressed] = useState(false);
    const [isServiceLimitsButtonPressed, setIsServiceLimitsButtonPressed] = useState(false);
    const [isMaxDaysButtonPressed, setIsMaxDaysButtonPressed] = useState(false);
    const [isCoinsuranceButtonPressed, setIsCoinsuranceButtonPressed] = useState(false);
    
    // Store original values when buttons are pressed
    const [originalDeductibleValues, setOriginalDeductibleValues] = useState({
        deductible: '',
        remaining: ''
    });
    
    const [originalOopmValues, setOriginalOopmValues] = useState({
        oopm: '',
        oopmRemaining: ''
    });
    
    const [originalServiceLimitsValues, setOriginalServiceLimitsValues] = useState({
        copay: '',
        maxDays: ''
    });
    
    const [originalMaxDaysValue, setOriginalMaxDaysValue] = useState('');
    const [originalCoinsuranceValue, setOriginalCoinsuranceValue] = useState('');

    const getNotesText = () => {
        if (formData.notes) {
            return formData.notes;
        }
        if (currentVob?.notes && currentVob.notes.length > 0) {
            return currentVob.notes[0].noteText;
        }
        return '';
    };

    // Handle numeric input changes with formatting
    const handleNumericChange = (field: keyof VOBFormData, value: string) => {
        if (value === '') {
            onInputChange(field, value);
            return;
        }
        const decimalFields: (keyof VOBFormData)[] = ['deductible', 'remaining', 'oopm', 'oopmRemaining'];
        const allowsDecimal = decimalFields.includes(field);
        

        const numericPattern = allowsDecimal 
            ? /^-?\d*\.?\d{0,2}$/
            : /^-?\d+$/;
        
        if (numericPattern.test(value)) {
            onInputChange(field, value);
        }
    };

    const getRawValue = (field: keyof VOBFormData): string => {
        const value = formData[field] as string;
        return value || '';
    };

    // Format displayed values - for other fields
    const getFormattedValue = (field: keyof VOBFormData): string => {
        const value = formData[field] as string;
        return formatNumberValue(value);
    };

    const handleNumericBlur = (field: keyof VOBFormData) => {
        const value = formData[field] as string;
        if (value) {
            const formatted = formatNumberValue(value);
            if (formatted !== value) {
                onInputChange(field, formatted);
            }
        }
    };

    // Handle deductible button click - toggle pressed state
    const handleDeductibleButtonClick = () => {
        if (!isDeductibleButtonPressed) {
            // Button is being pressed - store current values
            setOriginalDeductibleValues({
                deductible: formData.deductible,
                remaining: formData.remaining
            });
            
            // Clear the fields
            onInputChange('deductible', '');
            onInputChange('remaining', '');
        } else {
            // Button is being released - restore values if they were saved
            if (originalDeductibleValues.deductible) {
                onInputChange('deductible', originalDeductibleValues.deductible);
            }
            if (originalDeductibleValues.remaining) {
                onInputChange('remaining', originalDeductibleValues.remaining);
            }
        }
        
        // Toggle the button state
        setIsDeductibleButtonPressed(prev => !prev);
    };
    
    // Handle OOPM button click - toggle pressed state
    const handleOopmButtonClick = () => {
        if (!isOopmButtonPressed) {
            // Button is being pressed - store current values
            setOriginalOopmValues({
                oopm: formData.oopm,
                oopmRemaining: formData.oopmRemaining
            });
            
            // Clear the fields
            onInputChange('oopm', '');
            onInputChange('oopmRemaining', '');
        } else {
            // Button is being released - restore values if they were saved
            if (originalOopmValues.oopm) {
                onInputChange('oopm', originalOopmValues.oopm);
            }
            if (originalOopmValues.oopmRemaining) {
                onInputChange('oopmRemaining', originalOopmValues.oopmRemaining);
            }
        }
        
        // Toggle the button state
        setIsOopmButtonPressed(prev => !prev);
    };

    // Handle Service Limits button click - toggle pressed state
    const handleServiceLimitsButtonClick = () => {
        if (!isServiceLimitsButtonPressed) {
            // Button is being pressed - store current values
            setOriginalServiceLimitsValues({
                copay: formData.copay,
                maxDays: formData.maxDays
            });
            
            // Clear the fields
            onInputChange('copay', '');
            onInputChange('maxDays', '');
        } else {
            // Button is being released - restore values if they were saved
            if (originalServiceLimitsValues.copay) {
                onInputChange('copay', originalServiceLimitsValues.copay);
            }
            if (originalServiceLimitsValues.maxDays) {
                onInputChange('maxDays', originalServiceLimitsValues.maxDays);
            }
        }
        
        // Toggle the button state
        setIsServiceLimitsButtonPressed(prev => !prev);
    };

    // Handle Max Days button click - toggle pressed state
    const handleMaxDaysButtonClick = () => {
        if (!isMaxDaysButtonPressed) {
            // Button is being pressed - store current value
            setOriginalMaxDaysValue(formData.maxDays);
            
            // Clear the field
            onInputChange('maxDays', '');
        } else {
            // Button is being released - restore value if it was saved
            if (originalMaxDaysValue) {
                onInputChange('maxDays', originalMaxDaysValue);
            }
        }
        
        // Toggle the button state
        setIsMaxDaysButtonPressed(prev => !prev);
    };

    // Handle Coinsurance button click - toggle pressed state
    const handleCoinsuranceButtonClick = () => {
        if (!isCoinsuranceButtonPressed) {
            // Button is being pressed - store current value
            setOriginalCoinsuranceValue(formData.coinsurance);
            
            // Clear the field
            onInputChange('coinsurance', '');
        } else {
            // Button is being released - restore value if it was saved
            if (originalCoinsuranceValue) {
                onInputChange('coinsurance', originalCoinsuranceValue);
            }
        }
        
        // Toggle the button state
        setIsCoinsuranceButtonPressed(prev => !prev);
    };

    useEffect(() => {
        if (resetButtonStates) {
            setIsDeductibleButtonPressed(false);
            setIsOopmButtonPressed(false);
            setIsServiceLimitsButtonPressed(false);
            setIsMaxDaysButtonPressed(false);
            setIsCoinsuranceButtonPressed(false);
            setOriginalDeductibleValues({ deductible: '', remaining: '' });
            setOriginalOopmValues({ oopm: '', oopmRemaining: '' });
            setOriginalServiceLimitsValues({ copay: '', maxDays: '' });
            setOriginalMaxDaysValue('');
            setOriginalCoinsuranceValue('');
        }
    }, [resetButtonStates]);

    useEffect(() => {
        if (setButtonStates) {
            setIsDeductibleButtonPressed(setButtonStates.isDeductibleButtonPressed);
            setIsOopmButtonPressed(setButtonStates.isOopmButtonPressed);
            setIsServiceLimitsButtonPressed(setButtonStates.isServiceLimitsButtonPressed);
            setIsMaxDaysButtonPressed(setButtonStates.isMaxDaysButtonPressed);
            setIsCoinsuranceButtonPressed(setButtonStates.isCoinsuranceButtonPressed);
        }
    }, [setButtonStates]);



    return (
        <Grid container spacing={2} sx={{ mb: 2, mt: 2 }} test-id="vob-form-fields-container">
            {/* Deductible Fields */}
            <Grid item xs={12} container spacing={2} alignItems="stretch">
                <Grid item xs={4}>
                    <FramedSection test-id="vob-deductible-section">
                        <FrameLabel hasButton>Deductible</FrameLabel>
                        <FrameLabelButton 
                            test-id="vob-deductible-disable-button"
                            onClick={handleDeductibleButtonClick}
                            title={isDeductibleButtonPressed ? "Enable deductible fields" : "Disable deductible fields"}
                        >
                            <NoSymbol isPressed={isDeductibleButtonPressed} />
                        </FrameLabelButton>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                <VOBNumericField
                                    test-id="vob-deductible-total-input"
                                    fullWidth
                                    label="Total $"
                                    value={getRawValue('deductible')}
                                    onChange={e => handleNumericChange('deductible', e.target.value)}
                                    onBlur={() => handleNumericBlur('deductible')}
                                    error={deductibleError}
                                    helperText={deductibleError ? (deductibleErrorMessage || 'Either Deductible or Remaining must be filled') : ''}
                                    disabled={isDeductibleButtonPressed}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <VOBNumericField
                                    test-id="vob-deductible-remaining-input"
                                    fullWidth
                                    label="Remaining $"
                                    value={getRawValue('remaining')}
                                    onChange={e => handleNumericChange('remaining', e.target.value)}
                                    onBlur={() => handleNumericBlur('remaining')}
                                    error={remainingError}
                                    helperText={remainingError ? (remainingErrorMessage || 'Either Deductible or Remaining must be filled') : ''}
                                    disabled={isDeductibleButtonPressed}
                                />
                            </Grid>
                        </Grid>
                    </FramedSection>
                </Grid>
                
                {/* OOPM Fields and Deductible Included Checkbox */}
                <Grid item xs={8}>
                    <FramedSection test-id="vob-oopm-section">
                        <FrameLabel hasButton>Out of Pocket Maximum</FrameLabel>
                        <FrameLabelButton 
                            test-id="vob-oopm-disable-button"
                            onClick={handleOopmButtonClick}
                            title={isOopmButtonPressed ? "Enable OOPM fields" : "Disable OOPM fields"}
                        >
                            <NoSymbol isPressed={isOopmButtonPressed} />
                        </FrameLabelButton>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                <VOBNumericField
                                    test-id="vob-oopm-total-input"
                                    fullWidth
                                    label="Total $"
                                    value={getRawValue('oopm')}
                                    onChange={e => handleNumericChange('oopm', e.target.value)}
                                    onBlur={() => handleNumericBlur('oopm')}
                                    error={oopmError}
                                    helperText={oopmError ? (oopmErrorMessage || 'Either OOPM or OOP Remaining must be filled') : ''}
                                    disabled={isOopmButtonPressed}
                                />
                            </Grid>
                            <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                                <VOBNumericField
                                    test-id="vob-oopm-remaining-input"
                                    fullWidth
                                    label="Remaining $"
                                    value={getRawValue('oopmRemaining')}
                                    onChange={e => handleNumericChange('oopmRemaining', e.target.value)}
                                    onBlur={() => handleNumericBlur('oopmRemaining')}
                                    error={oopmRemainingError}
                                    helperText={oopmRemainingError ? (oopmRemainingErrorMessage || 'Either OOPM or OOP Remaining must be filled') : ''}
                                    disabled={isOopmButtonPressed}
                                />
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                <NoWrapFormControlLabel
                                    test-id="vob-deductible-included-checkbox"
                                    control={
                                        <Checkbox
                                            test-id="vob-deductible-included-checkbox-input"
                                            checked={formData.isDedIncOOPM}
                                            onChange={e => onInputChange('isDedIncOOPM', e.target.checked)}
                                            disabled={isOopmButtonPressed}
                                        />
                                    }
                                    label="Deductible Included"
                                />
                            </Grid>
                        </Grid>
                    </FramedSection>
                </Grid>
            </Grid>
            
            {/* Other Fields */}
            <Grid item xs={12} container spacing={2} alignItems="center">
                <Grid item xs={4}>
                    <FramedSection test-id="vob-copay-section">
                        <FrameLabel hasButton>Co-pay</FrameLabel>
                        <FrameLabelButton 
                            test-id="vob-copay-disable-button"
                            onClick={handleServiceLimitsButtonClick}
                            title={isServiceLimitsButtonPressed ? "Enable co-pay fields" : "Disable co-pay fields"}
                        >
                            <NoSymbol isPressed={isServiceLimitsButtonPressed} />
                        </FrameLabelButton>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                <VOBNumericField
                                    test-id="vob-copay-input"
                                    fullWidth
                                    label="Per Day/Visit $"
                                    value={getFormattedValue('copay')}
                                    onChange={e => handleNumericChange('copay', e.target.value)}
                                    error={copayError}
                                    helperText={copayError ? (copayErrorMessage || '') : ''}
                                    disabled={isServiceLimitsButtonPressed}
                                />
                            </Grid>
                            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                <LabelButtonWrapper>
                                    <FieldLabelButton
                                        test-id="vob-max-days-disable-button"
                                        onClick={handleMaxDaysButtonClick}
                                        title={isMaxDaysButtonPressed ? "Enable Max Days field" : "Disable Max Days field"}
                                        size="small"
                                    >
                                        <NoSymbol isPressed={isMaxDaysButtonPressed} />
                                    </FieldLabelButton>
                                    <VOBNumericField
                                        test-id="vob-max-days-input"
                                        fullWidth
                                        label="Max Days"
                                        value={getFormattedValue('maxDays')}
                                        onChange={e => handleNumericChange('maxDays', e.target.value)}
                                        error={maxDaysError}
                                        helperText={maxDaysError ? (maxDaysErrorMessage || '') : ''}
                                        disabled={isServiceLimitsButtonPressed || isMaxDaysButtonPressed}
                                        sx={{ 
                                            '& .MuiInputLabel-root': { 
                                                marginLeft: '16px' 
                                            }
                                        }}
                                    />
                                </LabelButtonWrapper>
                            </Grid>
                        </Grid>
                    </FramedSection>
                </Grid>
                <Grid item xs={4}>
                    <FramedSection test-id="vob-coinsurance-section">
                        <FrameLabel hasButton>Coinsurance</FrameLabel>
                        <FrameLabelButton 
                            test-id="vob-coinsurance-disable-button"
                            onClick={handleCoinsuranceButtonClick}
                            title={isCoinsuranceButtonPressed ? "Enable coinsurance field" : "Disable coinsurance field"}
                        >
                            <NoSymbol isPressed={isCoinsuranceButtonPressed} />
                        </FrameLabelButton>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                <VOBNumericField
                                    test-id="vob-coinsurance-input"
                                    fullWidth
                                    label="Patient %"
                                    value={getFormattedValue('coinsurance')}
                                    onChange={e => handleNumericChange('coinsurance', e.target.value)}
                                    error={coinsuranceError}
                                    helperText={coinsuranceError ? (coinsuranceErrorMessage || '') : ''}
                                    disabled={isCoinsuranceButtonPressed}
                                />
                            </Grid>
                            <Grid item xs={6}></Grid>
                        </Grid>
                    </FramedSection>
                </Grid>
                <Grid item xs={4}></Grid>
            </Grid>
            
            {/* Notes Field */}
            <Grid
                item
                direction="column"
                xs={12}
                style={{marginTop: '2px'}}
            >
                <TextField
                    test-id="vob-notes-input"
                    label="Notes"
                    name="noteText"
                    multiline
                    minRows={1}
                    maxRows={10}
                    value={getNotesText()}
                    style={{ display: "flex" }}
                    onChange={e => onInputChange('notes', e.target.value)}
                />
            </Grid>
        </Grid>
    );
}; 